import { useState } from 'react';
import { generateUploadSignedUrlForm } from 'api/internaltools';
import {
  FILE_EXTENSION_TO_INPUT_FORMAT_MAPPING,
  FILE_MIME_TYPE,
  FILE_SIZE,
  INPUT_FILE_FORMATS,
} from 'modules/merchantinfo/constants/constants';

const UPLOAD_FAILED_ERROR = 'Uploading failed!';

interface FileUploaderWrapperProps {
  id: string;
  maxSize?: number;
  acceptedFormats?: string;
  setError: (error: string) => void;
}

interface UploadFileResponseType {
  identifier: string;
  url: string;
  fileName: string;
  downloadableUrl: string;
  rawFile: File | null;
}

const useUploadFile: (
  params: FileUploaderWrapperProps
) => {
  uploadFile: (
    fileToUpload: File | null,
    bucket?: string,
    path?: string,
    progressCallback?: (fileName: string, progress: number) => void
  ) => Promise<UploadFileResponseType>;
  isLoading: boolean;
} = ({ maxSize = FILE_SIZE.THREE_MB, id, acceptedFormats = INPUT_FILE_FORMATS.PDF, setError }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const uploadFile = async (
    fileToUpload: File | null,
    bucket?: string,
    path?: string,
    progressCallback?: (fileName: string, progress: number) => void
  ): Promise<UploadFileResponseType> => {
    if (isLoading) {
      throw new Error('Upload already in progress');
    }

    if (!fileToUpload) {
      throw new Error('No file provided');
    }
    const fileExtension: string = fileToUpload?.name?.split('.')?.pop()?.toLowerCase() ?? '';

    const acceptedFormatsArr = acceptedFormats.split(',').map((item) => item.trim().toLowerCase());
    const mappedFormat =
      FILE_EXTENSION_TO_INPUT_FORMAT_MAPPING[fileExtension as keyof typeof FILE_EXTENSION_TO_INPUT_FORMAT_MAPPING];

    if (!acceptedFormatsArr.includes(mappedFormat)) {
      setError('* Invalid file format.');

      throw new Error('Invalid file format');
    }

    if (fileToUpload?.size > maxSize) {
      const err = `File size cannot exceed more than ${maxSize / FILE_SIZE.ONE_MB}MB`;

      setError(err);
      throw err;
    }

    //please add file type in FileMimeType on add new file format
    const fileName =
      id +
      '_' +
      Date.now() +
      '.' +
      (FILE_MIME_TYPE[fileToUpload?.type as keyof typeof FILE_MIME_TYPE] ?? fileExtension);

    const payload = {
      key: fileName,
      bucket,
      path,
    };

    try {
      setIsLoading(true);
      const response = await generateUploadSignedUrlForm(payload);

      if (!response) {
        throw new Error('Failed to get upload URL');
      }

      return new Promise<UploadFileResponseType>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.open('PUT', response?.data?.data?.url, true);
        xhr.setRequestHeader(
          'Content-Type',
          fileToUpload?.type || FILE_MIME_TYPE[fileExtension as keyof typeof FILE_MIME_TYPE]
        );

        xhr.upload.onprogress = function (event) {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;

            progressCallback?.(fileToUpload?.name, Math.floor(percentComplete));
          }
        };

        xhr.onload = function () {
          setIsLoading(false);
          if (xhr.status === 200) {
            setError('');
            const resolvedResponse: UploadFileResponseType = {
              ...response?.data,
              fileName: fileToUpload?.name,
            };

            resolve(resolvedResponse);
          } else {
            setError(UPLOAD_FAILED_ERROR);
            reject(UPLOAD_FAILED_ERROR);
          }
        };

        xhr.onerror = function () {
          setIsLoading(false);
          setError(UPLOAD_FAILED_ERROR);
          reject(UPLOAD_FAILED_ERROR);
        };

        xhr.send(fileToUpload);
      });
    } catch (error: any) {
      setIsLoading(false);
      setError(error?.message);
      throw error;
    }
  };

  return {
    isLoading,
    uploadFile,
  };
};

export default useUploadFile;
